/** An item's files, as its File tab shows them. */
import { useRef } from "preact/hooks";
import type { ItemDetail } from "../api/types.gen.ts";
import { ATTACHMENTS } from "./attach.ts";
import { docTypeOf } from "./documents.ts";

/** A file to show: the item's own, or one attached to it. */
export interface Shown {
  id: string;
  title: string;
  file: NonNullable<ItemDetail["file"]>;
  url: string | undefined;
  /** What to show instead of it (an iPhone photo's JPEG, made by the server), when there's one. */
  preview?: string | undefined;
  /** It as a PDF (a word-processing document's or presentation's, made by LibreOffice), once made. */
  pdf?: string | undefined;
  /** Its extracted text, when known (the item's own). */
  text?: string | null | undefined;
  /** Attached, not the item's own file. */
  attached: boolean;
}

/** An item's files: its own, then its attachments that are files (in their order). */
export function filesOf(item: ItemDetail): Shown[] {
  const own: Shown[] = item.file
    ? [
        {
          id: item.id,
          title: item.title ?? "",
          file: item.file,
          url: item.file_url ?? undefined,
          preview: item.preview_url ?? undefined,
          pdf: item.pdf_url ?? undefined,
          text: item.text,
          attached: false,
        },
      ]
    : [];
  const attached = (item.links[ATTACHMENTS] ?? []).flatMap((b): Shown[] =>
    b.file && !b.hidden
      ? [
          {
            id: b.id,
            title: b.title,
            file: b.file,
            url: b.url ?? undefined,
            preview: b.preview_url ?? undefined,
            pdf: b.pdf_url ?? undefined,
            attached: true,
          },
        ]
      : [],
  );
  return [...own, ...attached];
}

/**
 * A file's link, kept while its content stays the same: every reload of an item brings a fresh signed link, and a
 * new `src` would reload its preview (a PDF flickering). A new file (another `sha256`) takes its new link.
 */
export function useStableUrl(sha256: string, url: string | undefined): string | undefined {
  const kept = useRef<{ sha256: string; url: string | undefined }>({ sha256, url });
  if (kept.current.sha256 !== sha256 || !kept.current.url) {
    kept.current = { sha256, url };
  }
  return kept.current.url;
}

/** Image types few browsers show (iPhone photos): shown only through their preview. */
const UNSHOWN_IMAGES = new Set(["image/heic", "image/heif"]);

/**
 * The link to show a file as an image: its preview when it has one, else the file itself when it's an image every
 * browser shows; none: not an image to show (a HEIC photo whose JPEG isn't made yet).
 */
export function imageSrc(mime: string, url: string | undefined, preview: string | undefined): string | undefined {
  if (preview) {
    return preview;
  }
  return url && mime.startsWith("image/") && !UNSHOWN_IMAGES.has(mime) ? url : undefined;
}

/** An email's file type. */
export const EMAIL_MIME = "message/rfc822";

/** The item's own email (shown as a mail client shows it), not a file attached to it. */
export const isOwnEmail = (f: Shown): boolean => !f.attached && f.file.mime === EMAIL_MIME;

/**
 * The item's own document or spreadsheet, shown by Revisa's viewer (in its space, or on its page outside: a public
 * link's, a guest's, read-only), or its own PDF, shown by the PDF viewer: then it's the File tab's whole (a canvas
 * filling the window), the tab named after it and its facts and tools in the tab bar.
 */
export function ownDocument(item: ItemDetail): Shown | undefined {
  return filesOf(item).find(
    (f) =>
      !(f.attached || isOwnEmail(f)) &&
      (docTypeOf(f.file.mime) !== null || (f.file.mime === "application/pdf" && Boolean(f.url))),
  );
}
